// DimensionOverlay — DOM overlay for live measurement labels
// Shows dimensions, edge lengths, and area during drawing/resizing

import * as THREE from 'three'
import { formatDimensions, formatLength, formatArea } from '@/utils/areaCalculation'
import './DimensionOverlay.css'

type SlotName = 'dimensions' | 'edge' | 'area'

export class DimensionOverlay {
  private container: HTMLElement
  private canvas: HTMLCanvasElement
  private labels: Record<SlotName, HTMLElement>

  constructor(parentContainer: HTMLElement, canvas: HTMLCanvasElement) {
    this.canvas = canvas

    // Create overlay container
    this.container = document.createElement('div')
    this.container.className = 'dimension-overlay'
    parentContainer.appendChild(this.container)

    // Create label slots
    this.labels = {
      dimensions: this.createLabel(),
      edge: this.createLabel(),
      area: this.createLabel(),
    }
  }

  private createLabel(): HTMLElement {
    const el = document.createElement('div')
    el.className = 'dimension-label'
    this.container.appendChild(el)
    return el
  }

  /** Show "W × H m" label at a world position */
  showDimensions(widthM: number, heightM: number, worldX: number, worldZ: number, camera: THREE.Camera): void {
    const text = formatDimensions(widthM, heightM)
    this.labels.dimensions.textContent = text
    this.positionLabel(this.labels.dimensions, worldX, 0.02, worldZ, camera)
  }

  /** Show edge length label at a world position */
  showEdge(lengthM: number, worldX: number, worldZ: number, camera: THREE.Camera): void {
    this.labels.edge.textContent = formatLength(lengthM)
    this.positionLabel(this.labels.edge, worldX, 0.02, worldZ, camera)
  }

  /** Show area label at a world position */
  showArea(areaM2: number, worldX: number, worldZ: number, camera: THREE.Camera): void {
    this.labels.area.textContent = formatArea(areaM2)
    this.positionLabel(this.labels.area, worldX, 0.02, worldZ, camera)
  }

  /** Hide all labels */
  hideAll(): void {
    for (const label of Object.values(this.labels)) {
      label.classList.remove('visible')
    }
  }

  /** Hide a specific slot */
  hideSlot(slot: SlotName): void {
    this.labels[slot].classList.remove('visible')
  }

  private positionLabel(el: HTMLElement, worldX: number, worldY: number, worldZ: number, camera: THREE.Camera): void {
    const vec = new THREE.Vector3(worldX, worldY, worldZ)
    vec.project(camera)

    // Check if behind camera (NDC z range: -1 = near, +1 = far)
    if (vec.z < -1 || vec.z > 1) {
      el.classList.remove('visible')
      return
    }

    const rect = this.canvas.getBoundingClientRect()
    const x = (vec.x * 0.5 + 0.5) * rect.width
    const y = (-vec.y * 0.5 + 0.5) * rect.height

    el.style.left = `${x}px`
    el.style.top = `${y}px`
    el.classList.add('visible')
  }

  dispose(): void {
    this.container.remove()
  }
}
